import { useEffect, useRef, useState } from "react";

/**
 * Determine if a component should flash to indicate a change depending on a numerical property
 * @param dependsOn a number prop that you want to watch
 * @param duration the duration after which the old and new version of the dependsOn value are deemed to be the same
 * @returns [a boolean indicating if the component should flash, the old dependsOn value and the new dependsOn value]
 */
export const useShouldFlash = (
  dependsOn: number,
  duration = 300
): [boolean, { old: number; new: number }] => {
  const stableOldDependsOn = useRef<number>(dependsOn);
  const [isSame, setIsSame] = useState(true);

  useEffect(() => {
    if (dependsOn === stableOldDependsOn.current) {
      return;
    }

    setIsSame(false);

    const timeout = setTimeout(() => {
      stableOldDependsOn.current = dependsOn;
      setIsSame(true);
    }, duration);

    return () => clearTimeout(timeout);
  }, [dependsOn]);

  return [!isSame, { old: stableOldDependsOn.current, new: dependsOn }];
};
